import React, { useContext, useEffect, useMemo, useState } from 'react';
import { generatePath, Link } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import { getPharmacyLegalName } from "../../components/utils/utils";
import { routeConfigs } from "../../navigation/routeConfig";
import { CheckoutItem } from "./CheckoutItem";
import { CircularProgress } from "@mui/material";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import Tooltip from "@mui/material/Tooltip";
import { useQueryShippingRates } from "../../queries/shipping/hooks/useQueryShippingRates";
import { fedexShippingMap, getShippingRateRequestData, isRefrigerated } from "../../constants/shipping";
import { contextObject } from "../../components/shared/ShoppingCart/ShoppingCartContext";
import { NotificationType } from "../../constants/global";
import { ToasterContext } from "../../components/shared/Toaster/ToasterContext";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";

interface ICheckoutGroupItemProps {
  name: string;
  groupItem: any;
  shippingMethodsData: any;
  getGroupData: any;
  cartGroupData: any;
  setCartGroupData: any;
  setConfirmDisable: any;
  setFedexError: any;
  fedexError: any;
  freePriorityShippingApplied: boolean;
}
export const CheckoutGroupItem: React.FunctionComponent<ICheckoutGroupItemProps> = ({
  name,
  groupItem,
  shippingMethodsData,
  getGroupData,
  cartGroupData,
  setCartGroupData,
  setConfirmDisable,
  setFedexError,
  fedexError,
  freePriorityShippingApplied,
}) => {
  const { addToast } = useContext(ToasterContext);
  const { cart } = useContext(contextObject);
  const sellerUserId = groupItem[0]?.item?.User?.id;
  const cartLineProductIds = groupItem.map((c: any) => c.item.id).filter((pid: any) => pid != null).join(",");
  const sellerListingsTo = useMemo(() => {
    if (sellerUserId == null || Number.isNaN(Number(sellerUserId))) return null;
    const path = generatePath(routeConfigs.sellerListings.path, { sellerUserId: String(sellerUserId) });
    const qs = new URLSearchParams({
      pharmacy: getPharmacyLegalName(name),
      exclude: cartLineProductIds,
    });
    return `${path}?${qs.toString()}`;
  }, [sellerUserId, name, cartLineProductIds]);
  const isFrozen = groupItem.find((cartItem: any) => cartItem.item.isFrozen);
  const getCartItemTotal = (items: any) => {
    let total = 0;
    items.forEach((item: any) => {
      total = total + item.quantity * Number(item.item.price.replace(',', ''))
    });

    return total;
  }

  const isPriorityOvernightFreeEligible = () => {
    return getCartItemTotal(groupItem) >= 3000;
  }

  const defaultShippingMethod =
    isFrozen || isPriorityOvernightFreeEligible()
      ? 'fedex-priority-overnight'
      : shippingMethodsData[0].key
  const [shippingMethod, setShippingMethod] = useState<any>(defaultShippingMethod);

  const [getShippingRates, { data: shippingRatesData, loading: shippingRatesLoading }] = useQueryShippingRates({
    fetchPolicy: 'no-cache'
  });

  const getShippingMethods = (items: any, refrigerated: boolean) => {
    if (refrigerated || isPriorityOvernightFreeEligible()) {
      const item = items.find((shippingMethod: any) => shippingMethod.key === 'fedex-priority-overnight');

      return item ? [item] : items;
    }

    return items;
  }

  const getFedexNetCharge = (shippingRatesData: any) => {
    /* return shippingRatesData.getShippingRates?.output?.rateReplyDetails && shippingRatesData.getShippingRates.output.rateReplyDetails[0].ratedShipmentDetails
      ? shippingRatesData.getShippingRates.output.rateReplyDetails[0].ratedShipmentDetails[0].totalNetFedExCharge
      : 0; */
    const baseCharge = shippingRatesData.getShippingRates?.output?.rateReplyDetails && shippingRatesData.getShippingRates.output.rateReplyDetails[0].ratedShipmentDetails
      ? shippingRatesData.getShippingRates.output.rateReplyDetails[0].ratedShipmentDetails[0].totalNetFedExCharge
      : 0;

    // Add fixed surcharges based on shipping method
    switch (shippingMethod) {
      case 'fedex-ground':
        return baseCharge + 7;
      case 'fedex-2-day':
        return baseCharge + 12;
      case 'fedex-priority-overnight':
        return baseCharge + 20
      default:
        return baseCharge;
    }
  }

  const getCartTotal = (fedexNet: any, cartItemTotal: any) => {
    return fedexNet + cartItemTotal
  }

  useEffect(() => {
    getShippingRates({
      variables: {
        shippingRates: getShippingRateRequestData(
          groupItem[0].item.Pharmacy,
          groupItem[0].user.PharmacyUsers[0].Pharmacy,
          isFrozen ? 'PRIORITY_OVERNIGHT' : fedexShippingMap[shippingMethod],
          groupItem.length
        )
      },
    })
  }, [getShippingRates, shippingMethod, isFrozen, cart]);

  useEffect(() => {
    if (shippingRatesData) {
      if (!shippingRatesData.getShippingRates?.output?.rateReplyDetails && shippingRatesData.getShippingRates?.output?.alerts.length > 0) {
        setFedexError({
          ...fedexError,
          [name]: true
        })
        addToast({ type: NotificationType.Error, title: 'Something went wrong, please contact to "support@tradenetrx.com".' });
      } else {
        delete fedexError[name];
      }
      getGroupData({
        total: +getFedexNetCharge(shippingRatesData) + getCartItemTotal(groupItem),
        fee: +getFedexNetCharge(shippingRatesData),
        key: `${groupItem[0].item.Pharmacy.legalName}--${groupItem[0].item.Pharmacy.id}`,
        shippingMethod: shippingMethodsData.find((item: any) => item.key === shippingMethod),
        groupItem: groupItem,
        pharmacyId: groupItem[0].item.Pharmacy.id,
      });
    }
  }, [shippingRatesData, cart]);

  useEffect(() => {
    setConfirmDisable(shippingRatesLoading);
  }, [shippingRatesLoading]);

  useEffect(() => {
    if (
      (isPriorityOvernightFreeEligible() || isFrozen || isRefrigerated(groupItem)) &&
      shippingMethod !== 'fedex-priority-overnight'
    ) {
      setShippingMethod('fedex-priority-overnight');
    }
  }, [groupItem, isFrozen, shippingMethod]);



  return (
    <Paper key={name} sx={{ mb: 2, display: 'flex', flexDirection: 'column' }}>
      <Box>
        <Divider />
        {sellerListingsTo && (
          <Box
            sx={{
              px: 2,
              py: 1.5,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 1,
              bgcolor: 'grey.50',
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            <Button
              component={Link}
              to={sellerListingsTo}
              variant="contained"
              size="small"
              sx={{
                bgcolor: "grey.900",
                color: "common.white",
                boxShadow: "none",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  bgcolor: "grey.800",
                  boxShadow: "none",
                },
              }}
            >
              Seller&apos;s other listings
            </Button>
          </Box>
        )}
        {groupItem.map((cartItem: any) => (
          <CheckoutItem
            key={cartItem.id}
            cartItem={cartItem}
            cartGroupData={cartGroupData}
            setCartGroupData={setCartGroupData}
            shippingRatesLoading={shippingRatesLoading}
          />
        ))}
        <Box sx={{ display: 'flex', alignItems: 'center', p: 2, justifyContent: 'space-between', fontSize: 12 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={6} lg={9} xl={9}>
              <Box sx={{ display: 'flex', mt: 2, alignItems: 'center' }}>
                <Grid item xs={12} sm={12} md={12} lg={2} xl={2}>Shipping Method</Grid>
                <Grid item xs={12} sm={12} md={12} lg={10} xl={10}>
                  <RadioGroup
                    row
                    id='shippingMethod'
                    value={shippingMethod}
                    onChange={(event) => setShippingMethod(event.target.value)}
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: 12 } }}
                  >
                    {getShippingMethods(shippingMethodsData, isRefrigerated(groupItem)).map((shippingMethod: any) => (
                      <FormControlLabel
                        disabled={shippingRatesLoading}
                        key={shippingMethod.id}
                        value={shippingMethod.key}
                        control={<Radio />}
                        label={(
                          <Box sx={{ position: 'relative', pr: 6, minWidth: 120 }}>
                            <Box>{shippingMethod.name}</Box>
                            {shippingMethod.key === 'fedex-priority-overnight' && isPriorityOvernightFreeEligible() && (
                              <Tooltip title="Free Priority Shipping on Orders $3000 +" arrow placement="right">
                                <Chip
                                  label="FREE"
                                  size="small"
                                  sx={{
                                    position: 'absolute',
                                    top: -8,
                                    right: 0,
                                    height: 20,
                                    fontSize: 11,
                                    fontWeight: 'bold',
                                    color: '#fff',
                                    bgcolor: '#2e7d32',
                                    cursor: 'help',
                                  }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        )}
                      />
                    ))}
                  </RadioGroup>
                </Grid>
              </Box>
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>Item Total</Box>
                  <Box>${getCartItemTotal(groupItem).toFixed(2)}</Box>
                </Box>
                <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>Shipping</Box>
                  {(shippingRatesLoading || !shippingRatesData) ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {freePriorityShippingApplied && shippingMethod !== "fedex-ground" && (
                        <Box sx={{ textDecoration: 'line-through', opacity: 0.75 }}>
                          ${getFedexNetCharge(shippingRatesData).toFixed(2)}
                        </Box>
                      )}
                      <Box>{(shippingMethod === "fedex-ground" || freePriorityShippingApplied) ? "$0.00" : `$${getFedexNetCharge(shippingRatesData).toFixed(2)}`}</Box>
                    </Box>
                  )}
                </Box>
                {freePriorityShippingApplied && (
                  <Box sx={{ mb: 1, color: '#2e7d32', fontSize: 11, fontWeight: 'bold', textAlign: 'right' }}>
                    Free Priority Shipping applied
                  </Box>
                )}
                <Divider />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>Total</Box>
                  {(shippingRatesLoading || !shippingRatesData) ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <Box>{(shippingMethod === "fedex-ground" || freePriorityShippingApplied)
                      ? `$${getCartItemTotal(groupItem).toFixed(2)}`
                      : `$${getCartTotal(+getFedexNetCharge(shippingRatesData), getCartItemTotal(groupItem)).toFixed(2)}`
                    }</Box>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Paper>
  );
}

export default CheckoutGroupItem;